import { Box, Heading, VStack } from '@chakra-ui/react';
import Editor from '@monaco-editor/react';
import { useMemo } from 'react';

import { useColorMode } from '#src/components/ui/color-mode';

function normalizeToData(value: unknown): unknown {
  if (value === undefined) return null;
  if (typeof value === 'string') {
    try {
      return JSON.parse(value) as unknown;
    } catch {
      return value;
    }
  }
  return value;
}

/**
 * Pretty view for audit JSON: real newlines inside string values are shown as separate lines
 * (not as \\n in one mega-line). Long single-line strings are left for Monaco to soft-wrap.
 * Output is JSON-like but not always valid JSON when strings are expanded.
 */
function formatJsonReadable(value: unknown, indent: number): string {
  const pad = '  '.repeat(indent);
  const pad1 = '  '.repeat(indent + 1);

  if (value === null) return 'null';
  if (typeof value === 'boolean' || typeof value === 'number') {
    return JSON.stringify(value);
  }
  if (typeof value === 'string') {
    if (!value.includes('\n')) return JSON.stringify(value);
    const lines = value.split('\n');
    return `\n${pad1}${lines.join(`\n${pad1}`)}`;
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return '[]';
    const parts = value.map((item, i) => {
      const formatted = formatJsonReadable(item, indent + 1);
      const body = formatted.startsWith('\n') ? formatted : ` ${formatted}`;
      return `${pad1}[${i}]:${body}`;
    });
    return `[\n${parts.join(',\n')}\n${pad}]`;
  }
  if (typeof value === 'object') {
    const o = value as Record<string, unknown>;
    const keys = Object.keys(o);
    if (keys.length === 0) return '{}';
    const parts = keys.map((k) => {
      const v = o[k];
      const formatted = formatJsonReadable(v, indent + 1);
      const key = JSON.stringify(k);
      if (formatted.startsWith('\n')) {
        return `${pad1}${key}:${formatted}`;
      }
      return `${pad1}${key}: ${formatted}`;
    });
    return `{\n${parts.join(',\n')}\n${pad}}`;
  }
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function jsonToEditorText(value: unknown): string {
  const data = normalizeToData(value);
  try {
    return formatJsonReadable(data, 0);
  } catch {
    try {
      return JSON.stringify(data, null, 2);
    } catch {
      return String(data);
    }
  }
}

interface JsonReadonlyBlockProps {
  title: string;
  value: unknown;
  height?: string;
}

export function JsonReadonlyBlock({
  title,
  value,
  height = 'min(50vh, 480px)',
}: JsonReadonlyBlockProps) {
  const { colorMode } = useColorMode();
  const monacoTheme = colorMode === 'dark' ? 'vs-dark' : 'vs-light';
  const text = useMemo(() => jsonToEditorText(value), [value]);

  return (
    <VStack gap={2} align="stretch">
      <Heading size="sm" color="fg">
        {title}
      </Heading>
      <Box
        borderWidth="1px"
        borderRadius="md"
        borderColor="border"
        overflow="hidden"
      >
        <Editor
          height={height}
          defaultLanguage="plaintext"
          theme={monacoTheme}
          value={text}
          options={{
            readOnly: true,
            minimap: { enabled: false },
            wordWrap: 'on',
            wrappingStrategy: 'advanced',
            stickyScroll: { enabled: false },
            scrollBeyondLastLine: false,
            folding: true,
            lineNumbers: 'on',
            renderLineHighlight: 'none',
          }}
        />
      </Box>
    </VStack>
  );
}
